import {
  Box,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import React, { useState } from 'react';
import { BackButton, Button, ErrorMessage, Heading, Select } from '../../components/Common';
import { useNavigate } from 'react-router-dom';
import { countryList } from '../../helper';
import { useMessage } from '../../components/Common/Message/MessageContext';
import {
  useGetCityListQuery,
  useGetStateListQuery,
  useCreatePincodeMutation,
  useGetExtraPincodesByCityQuery,
  useDeleteExtraPincodeMutation,
} from '../../services/Location';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const CreatePincode = () => {
  const { showMessage } = useMessage();
  const navigate = useNavigate();
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(null);
  const [city, setCity] = useState(null);
  const [pincodeInput, setPincodeInput] = useState('');
  const [pincodes, setPincodes] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingPincodeValue, setEditingPincodeValue] = useState('');

  const [errorMessages, setErrorMessages] = useState({});

  const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;
  const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
  const stateList = stateLst?.data;

  const [createPincode] = useCreatePincodeMutation();
  const { data: extraPincodeRes, isLoading: extraPincodeLoading } = useGetExtraPincodesByCityQuery(city, {
    skip: !city,
  });
  const [deleteExtraPincode, { isLoading: deleteLoading }] = useDeleteExtraPincodeMutation();

  const extraPincodes = extraPincodeRes?.data || [];

  const handleStateChange = (e) => {
    setStates(e.target.value);
    setCity(null); // Reset city when state changes
    setPincodes([]); // Reset pincodes when state changes
  };
  const handleCountryChange = (e) => {
    setCountry(e.target.value);
    setStates(null); // Reset state when country changes
    setCity(null); // Reset city when country changes
    setPincodes([]); // Reset pincodes when country changes
  };
  const handleCityChange = (e) => {
    setCity(e.target.value);
    setPincodes([]); // Reset pincodes when city changes
    setEditingRowId(null);
    setEditingPincodeValue('');
  };

  const validateField = (field, fieldName) => {
    if (!field) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
      return true;
    }
    setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
    return false;
  };

  const handlePincodeInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 6 digits
    if (/^\d{0,6}$/.test(value)) {
      setPincodeInput(value);
      // Clear error when user is typing
      if (value.length === 6) {
        setErrorMessages(prev => ({ ...prev, pincodeInput: '' }));
      }
    }
  };

  const handleAddPincode = () => {
    if (pincodeInput.trim() === '') {
      setErrorMessages(prev => ({ ...prev, pincodeInput: 'Pincode cannot be empty' }));
      return;
    }

    if (pincodeInput.trim().length !== 6) {
      setErrorMessages(prev => ({ ...prev, pincodeInput: 'Pincode must be exactly 6 digits' }));
      return;
    }

    if (!pincodes.includes(pincodeInput.trim())) {
      setPincodes([...pincodes, pincodeInput.trim()]);
      setPincodeInput('');
      setErrorMessages(prev => ({ ...prev, pincodeInput: '' }));
    } else {
      setErrorMessages(prev => ({ ...prev, pincodeInput: 'This pincode is already added' }));
    }
  };

  const handleRemovePincode = (pincodeToRemove) => {
    setPincodes(pincodes.filter(pincode => pincode !== pincodeToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPincode();
    }
  };

  const handleDeleteExistingPincode = async (id) => {
    try {
      const res = await deleteExtraPincode(id).unwrap();
      const { status, message } = res || {};
      if (status) {
        showMessage('success', message || 'Pincode deleted successfully');
      } else {
        showMessage('error', message || 'Failed to delete pincode');
      }
    } catch (error) {
      console.error('Failed to delete pincode:', error);
      showMessage('error', error?.data?.message || 'Failed to delete pincode');
    }
  };

  const handleStartEditExisting = (row) => {
    setEditingRowId(row.id);
    setEditingPincodeValue(row.pincode);
  };

  const handleCancelEditExisting = () => {
    setEditingRowId(null);
    setEditingPincodeValue('');
  };

  const handleSaveEditExisting = async () => {
    if (!editingRowId) return;

    const newPin = editingPincodeValue?.toString().trim();

    if (!newPin) {
      showMessage('error', 'Pincode cannot be empty');
      return;
    }

    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      showMessage('error', 'Pincode must be exactly 6 digits');
      return;
    }

    if (!country || !states || !city) {
      showMessage('error', 'Please select country, state and city before updating pincode');
      return;
    }

    // Find the current pincode being edited
    const currentPincode = extraPincodes.find(p => p.id === editingRowId);
    
    // If pincode hasn't changed, just close the edit mode
    if (currentPincode && currentPincode.pincode === newPin) {
      setEditingRowId(null);
      setEditingPincodeValue('');
      showMessage('info', 'No changes made');
      return;
    }

    // Backend CreatePincode expects JSON body (no multipart/form-data)
    const payload = {
      country,
      state: states,
      city,
      pincode: [newPin],
    };

    try {
      // Try to create new pincode first (this will fail if duplicate exists)
      const response = await createPincode(payload).unwrap();
      const { status, message } = response || {};

      if (status) {
        // Only if creation succeeds, delete the old one
        await deleteExtraPincode(editingRowId).unwrap();
        showMessage('success', message || 'Pincode updated successfully');
        setEditingRowId(null);
        setEditingPincodeValue('');
      } else {
        showMessage('error', message || 'Failed to update pincode');
      }
    } catch (error) {
      console.error('Failed to update pincode:', error);
      // Surface backend validation / duplicate errors clearly
      const errorMessage =
        error?.data?.message ||
        error?.error ||
        'An error occurred while updating pincode';
      showMessage('error', errorMessage);
    }
  };

  const handleFormSubmit = async () => {
    // Reset all error messages
    setErrorMessages({});

    // Validate pincodes
    if (pincodes.length === 0) {
      setErrorMessages(prev => ({ ...prev, pincodes: 'Please add at least one pincode.' }));
      return;
    } else {
      // Additional validation to ensure all pincodes are 6 digits
      const invalidPincodes = pincodes.some(p => p.length !== 6);
      if (invalidPincodes) {
        setErrorMessages(prev => ({ ...prev, pincodes: 'All pincodes must be 6 digits' }));
        return;
      } else {
        setErrorMessages(prev => ({ ...prev, pincodes: '' }));
      }
    }

    // Validate fields
    const countryError = validateField(country, 'country');
    const stateError = validateField(states, 'state');
    const cityError = validateField(city, 'city');

    // If any field has an error, prevent form submission
    if (countryError || stateError || cityError) {
      return;
    }

    // Backend CreatePincode expects JSON body, not multipart/form-data
    const payload = {
      country,
      state: states,
      city,
      pincode: pincodes,
    };

    try {
      const response = await createPincode(payload).unwrap();
      const { status, message } = response || {};

      if (status) {
        navigate('/admin');
        showMessage('success', message || 'Pincodes created successfully');
      } else {
        showMessage('error', message || 'Failed to create pincodes');
      }
    } catch (error) {
      console.error('Failed to create pincodes:', error);
      // Show backend message (e.g., "Pincode(s) already exist: 394101")
      const errorMessage =
        error?.data?.message ||
        error?.error ||
        'An error occurred while creating pincodes';
      showMessage('error', errorMessage);
    }
  };

  return (
    <>
      <Stack flexDirection={'row'} gap={1} mb={1}>
        <BackButton />
        <Heading head='Create Pincode' />
      </Stack>
      <Box className='card_container'>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select 
              selectList={countryList} 
              labelinput='Country' 
              name="country" 
              value={country} 
              onChange={handleCountryChange}
              disabled={true}
            />
            {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select 
              selectList={stateLoading ? [] : stateList} 
              labelinput='State' 
              name="state" 
              value={states} 
              onChange={handleStateChange}
            />
            {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select 
              selectList={cityLoading ? [] : cityList} 
              labelinput='City' 
              name="city" 
              value={city} 
              onChange={handleCityChange}
            />
            {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
          </Grid>

          {/* Pincode Input Field */}
          <Grid item xs={12} sm={6}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
              Add Pincodes
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                type='number'
                value={pincodeInput}
                onChange={handlePincodeInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter 6-digit pincode"
                inputProps={{ maxLength: 6 }}
                error={!!errorMessages.pincodeInput}
                helperText={errorMessages.pincodeInput}
              />
              <Button
                buttonName="Add"
                onClick={handleAddPincode}
                disabled={!pincodeInput.trim() || pincodeInput.trim().length !== 6}
                sx={{ minWidth: '100px' }}
              />
            </Stack>
            {errorMessages.pincodes && <ErrorMessage message={errorMessages.pincodes} />}
            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
              {pincodes.map((pincode) => (
                <Chip
                  key={pincode}
                  label={pincode}
                  onDelete={() => handleRemovePincode(pincode)}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Grid>

          {/* Existing pincodes table for selected city */}
          {city && (
            <Grid item xs={12}>
              <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={1}>
                Existing Pincodes for Selected City
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pincode</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {extraPincodeLoading ? (
                      <TableRow>
                        <TableCell colSpan={2}>Loading pincodes...</TableCell>
                      </TableRow>
                    ) : extraPincodes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>No pincodes found for this city.</TableCell>
                      </TableRow>
                    ) : (
                      extraPincodes.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {editingRowId === row.id ? (
                              <TextField
                                size="small"
                                value={editingPincodeValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d{0,6}$/.test(val)) {
                                    setEditingPincodeValue(val);
                                  }
                                }}
                                placeholder="Enter 6-digit pincode"
                                type="number"
                                inputProps={{ maxLength: 6 }}
                              />
                            ) : (
                              row.pincode
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingRowId === row.id ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  buttonName="Save"
                                  onClick={handleSaveEditExisting}
                                  disabled={!editingPincodeValue || editingPincodeValue.length !== 6}
                                />
                                <Button
                                  buttonName="Cancel"
                                  color="white"
                                  onClick={handleCancelEditExisting}
                                />
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEditExisting(row)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteExistingPincode(row.id)}
                                  disabled={deleteLoading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack flexDirection={'row'} gap={1}>
              <Button buttonName='save' onClick={handleFormSubmit} />
              <Button buttonName='cancel' color='white' onClick={() => navigate('/admin')} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CreatePincode;
